import React from 'react'

import SafeCheckReportButton from 'components/SafeCheckReportButton'
import useGoogleTagManager from 'hooks/useGoogleTagManager'
import useSafeCheck from 'hooks/useSafeCheck'
import useSession from 'hooks/useSession'
import useUser from 'hooks/useUser'
import AssetType from 'state/enums/AssetType'

type Props = {
    applicationAssetType: AssetType
    applicationSource: string
    applicationStatus: string
    applicationID: number
    applicationUUID: string
    isPreapprovalApplication?: boolean
}

const getSafeCheckReportUrl = (
    applicationID: number,
    applicationSource: string,
    applicationStatus: string,
    applicationUUID: string,
    isPreapprovalApplication?: boolean,
) => {
    const base = `/#/safecheck/report/${applicationSource}/${applicationID}/${applicationUUID}/${applicationStatus}`

    // isPreapproval=1 is read by SafeCheckReportPage to disable resubmission CTAs
    // for pre-approval applications without requiring an extra API call.
    return isPreapprovalApplication ? `${base}?isPreapproval=1` : base
}

export default function SafeCheckReportButtonContainer({
    applicationAssetType,
    applicationSource,
    applicationStatus,
    applicationID,
    applicationUUID,
    isPreapprovalApplication,
}: Props): JSX.Element {
    const { user } = useUser()
    const googleTagManager = useGoogleTagManager()
    const session = useSession()
    const requireMFA = !session.mfaTimeLeft

    const safeCheckReportUrl = getSafeCheckReportUrl(
        applicationID,
        applicationSource,
        applicationStatus,
        applicationUUID,
        isPreapprovalApplication,
    )

    const { canUseSafeCheck, hasSafeCheckReport, isLoading, safeCheckVisible } =
        useSafeCheck(applicationUUID, applicationAssetType)

    const buttonText = 'View Credit Report'

    const disabledButtonText = isLoading
        ? 'Looking for Credit Report...'
        : 'Credit Report Not Available'

    if (!safeCheckVisible || !canUseSafeCheck || !user?.mfaVerified) {
        return <div />
    }

    return (
        <SafeCheckReportButton
            user={user}
            hasSafeCheckReport={hasSafeCheckReport}
            safeCheckReportUrl={safeCheckReportUrl}
            buttonText={buttonText}
            disabledButtonText={disabledButtonText}
            requireMFA={requireMFA}
            onReportOpen={() => {
                googleTagManager.trackEvent({
                    event: 'view_credit_report',
                    link_text: buttonText,
                    link_url: safeCheckReportUrl,
                    application_id: applicationUUID,
                    custom_events: {
                        credit_report_views: 1,
                    },
                })
            }}
        />
    )
}
